import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Allows only one FAQ accordion to remain open at a time.
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles accordion click.
   * For the FAQ section, only one question can be open at a time.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Find the Shopify section containing this accordion.
    const section = this.closest('.shopify-section');

    if (!section) return;

    // Detect the FAQ section by its heading.
    const heading = section.querySelector('h1, h2, h3, h4, h5, h6');

    const isFAQ =
      heading &&
      heading.textContent.toLowerCase().includes('frequently asked questions');

    if (!isFAQ) return;

    // Close every other FAQ accordion in this section.
    const otherAccordions = section.querySelectorAll('accordion-custom');

    otherAccordions.forEach((accordion) => {
      if (accordion !== this) {
        const details = accordion.querySelector('details');

        if (details) {
          details.open = false;
        }
      }
    });
  };

  /**
   * Handles media query changes.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets default accordion state.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles Escape key.
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}